import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { stageLabel, stageTone } from '../lib/stage';

const API_BASE = 'https://visionry-api-production.up.railway.app';

// The bot's WhatsApp number for the shareable join link. Env-overridable so a
// number change never needs a code change.
const WA_NUMBER = process.env.REACT_APP_WA_NUMBER || '353851714075';

// ── Brand tokens (match visionry.io) ──
const C = {
  bg:       '#061618',
  card:     '#0d3338',
  card2:    '#10434a',
  border:   '#10434a',
  teal:     '#25a2b3',
  tealSoft: '#5fc4d2',
  text:     '#e0f0f2',
  muted:    '#7fb8c0',
  faint:    '#3d5a5f',
  green:    '#00cc88',
  greenBg:  '#06231a',
  red:      '#ff6b6b',
  redBg:    '#2a0f0f',
  amber:    '#ffb84d',
  amberBg:  '#2a1f0a'
};

// Mobile breakpoint — matches Students.js.
const MOBILE_MAX = 640;
function useIsMobile() {
  const [m, setM] = useState(typeof window !== 'undefined' ? window.innerWidth <= MOBILE_MAX : false);
  useEffect(() => {
    const onR = () => setM(window.innerWidth <= MOBILE_MAX);
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
  }, []);
  return m;
}

// Shared action button — same 44px touch-target pattern as Students.js.
function Btn({ children, onClick, color = C.muted, border = C.border, bg, disabled, fill, filled, as, href }) {
  const style = {
    flex: fill ? 1 : 'none',
    background: filled ? bg : 'none',
    border: filled ? 'none' : `1px solid ${border}`,
    borderRadius: 10,
    color, fontSize: 14, fontWeight: 600, padding: '11px 16px',
    minHeight: 44, whiteSpace: 'nowrap', textAlign: 'center',
    textDecoration: 'none', display: 'inline-flex',
    alignItems: 'center', justifyContent: 'center',
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1
  };
  if (as === 'a') {
    return <a href={href} target="_blank" rel="noreferrer" style={style}>{children}</a>;
  }
  return <button onClick={onClick} disabled={disabled} style={style}>{children}</button>;
}

// "3 days ago" / "today" style relative wait time.
function waitedSince(iso) {
  if (!iso) return '';
  const then = new Date(iso);
  const days = Math.floor((Date.now() - then.getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  const weeks = Math.floor(days / 7);
  return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
}

// Qualification display labels (mirror the bot's stored values). The
// how-far-along chip is country-dependent and lives in ../lib/stage.
const VEHICLE_LABEL = { instructor: "Instructor's car", student: 'Own car' };
const GEAR_LABEL = { manual: 'Manual', automatic: 'Automatic' };
const INSTR_PREF_LABEL = { male: 'Prefers male instructor', female: 'Prefers female instructor', any: 'Any instructor' };

// Drop 8 qualification chips — shown alongside the original area/EDT/availability
// ones, same conditional render-if-present pattern.
function QualificationChips({ lead }) {
  return (
    <>
      {lead.learner_permit  && <Chip tone="plain">🪪 Permit: {lead.learner_permit}</Chip>}
      {lead.driver_number   && <Chip tone="plain">🆔 {lead.driver_number}</Chip>}
      {lead.exam_booked     && <Chip tone="plain">📝 Test booked: {lead.exam_booked}</Chip>}
      {lead.lesson_type     && <Chip tone="plain">📋 {lead.lesson_type}</Chip>}
      {lead.vehicle_pref    && <Chip tone="plain">🚗 {VEHICLE_LABEL[lead.vehicle_pref] || lead.vehicle_pref}</Chip>}
      {lead.gear_pref       && <Chip tone="plain">⚙ {GEAR_LABEL[lead.gear_pref] || lead.gear_pref}</Chip>}
      {lead.instructor_pref && <Chip tone="plain">👤 {INSTR_PREF_LABEL[lead.instructor_pref] || lead.instructor_pref}</Chip>}
    </>
  );
}

function Chip({ children, tone }) {
  const tones = {
    teal:  { bg: C.card2,   fg: C.tealSoft },
    amber: { bg: C.amberBg, fg: C.amber },
    plain: { bg: C.card2,   fg: C.muted }
  };
  const t = tones[tone] || tones.plain;
  return (
    <span style={{
      background: t.bg, color: t.fg, borderRadius: 6,
      fontSize: 11.5, fontWeight: 600, padding: '3px 8px',
      whiteSpace: 'nowrap'
    }}>
      {children}
    </span>
  );
}

// ── Share card: the instructor's one link. WHY it lives here and not Settings:
// the Waitlist tab is the waitlist-mode home screen, and the link IS the
// product — it must be one tap away, not buried. ──
export function ShareCard({ instructor, isMobile }) {
  const [copied, setCopied] = useState(false);
  const link = `https://wa.me/${WA_NUMBER}?text=${instructor.instructor_key}`;

  async function copy() {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked — the link is visible to copy manually */ }
  }

  return (
    <div style={{
      background: C.card, border: `1px solid ${C.teal}`, borderRadius: 14,
      padding: 16, marginBottom: 18
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 4 }}>
        Your waitlist link
      </div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
        Share this anywhere — Facebook bio, Google listing, or as your reply when
        someone asks for lessons. It takes their details and adds them to your list.
      </div>
      <div style={{
        display: 'flex', flexDirection: isMobile ? 'column' : 'row',
        gap: 8, alignItems: 'stretch'
      }}>
        <div style={{
          flex: 1, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10,
          padding: '11px 12px', fontSize: 12.5, color: C.tealSoft,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          fontFamily: 'monospace'
        }}>
          {link}
        </div>
        <Btn onClick={copy} filled bg={C.teal} color={C.bg} fill={isMobile}>
          {copied ? 'Copied ✓' : 'Copy link'}
        </Btn>
      </div>
    </div>
  );
}

export default function Waitlist({ instructor }) {
  const [leads, setLeads]           = useState(null);   // status 'waiting'
  const [accepted, setAccepted]     = useState([]);      // status 'accepted' — the handoff history
  const [view, setView]             = useState('waiting'); // 'waiting' | 'accepted'
  const [busy, setBusy]             = useState(null);   // id being actioned
  const [confirming, setConfirming] = useState(null);   // { id, action: 'accept' | 'remove' }
  const [note, setNote]             = useState(null);
  const isMobile = useIsMobile();

  const isWaitlistMode = instructor.mode === 'waitlist';

  const load = useCallback(async () => {
    const { data } = await supabase
      .from('waitlist')
      .select('id, wa_id, name, area, edt_progress, lesson_stage, availability, avail_detail, learner_permit, driver_number, exam_booked, lesson_type, vehicle_pref, gear_pref, instructor_pref, signup_completed_at, status, created_at, last_confirmed_at, offered_at, updated_at')
      .eq('instructor_key', instructor.instructor_key)
      .in('status', ['waiting', 'accepted'])
      .order('created_at', { ascending: true });
    const all = data || [];
    setLeads(all.filter(l => l.status === 'waiting'));   // queue: oldest first
    setAccepted(
      all.filter(l => l.status === 'accepted')
         .sort((a, b) => new Date(b.offered_at || b.updated_at || b.created_at) - new Date(a.offered_at || a.updated_at || a.created_at))
    );                                                    // history: newest first
  }, [instructor.instructor_key]);

  useEffect(() => { load(); }, [load]);

  async function accept(lead) {
    setBusy(lead.id);
    setNote(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API_BASE}/accept-waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({ waitlist_id: lead.id })
      });
      const body = await res.json().catch(() => ({}));

      if (!res.ok) {
        setNote({ type: 'err', text: body.error || 'Could not accept this lead.' });
        setBusy(null);
        return;
      }

      const who = lead.name ? lead.name.split(' ')[0] : 'They';

      if (body.already_student) {
        // HONESTY FIX: previously this rendered as a success invite. Nothing
        // was sent — the person is already registered under another instructor.
        setNote({
          type: 'warn',
          text: `${who} is already registered on Visionry with another instructor, so no invite was sent. Best to message them directly: wa.me/${lead.wa_id}`
        });
      } else if (body.handoff) {
        setNote({
          type: 'ok',
          text: body.notified === false
            ? `${who} is marked accepted, but the message didn't send — contact them directly on wa.me/${lead.wa_id} to arrange the first lesson.`
            : `${who} has been told a space opened. 🎉 Their number: wa.me/${lead.wa_id} — message them to arrange the first lesson.`
        });
      } else {
        setNote({
          type: 'ok',
          text: body.notified === false
            ? `${who} added as a student — but the invite message couldn't send. They can still start by messaging your code.`
            : `${who} has been invited to start lessons. 🎉`
        });
      }
      setConfirming(null);
      await load();
    } catch {
      setNote({ type: 'err', text: 'Network error — please try again.' });
    }
    setBusy(null);
  }

  // Remove: soft flip to 'removed' straight through Supabase (RLS-scoped).
  // WHY it exists: without it an instructor literally cannot clear a
  // time-waster — the list only ever grows, and a list you can't curate is a
  // list you stop trusting.
  async function remove(lead) {
    setBusy(lead.id);
    setNote(null);
    const { error } = await supabase
      .from('waitlist')
      .update({ status: 'removed', updated_at: new Date().toISOString() })
      .eq('id', lead.id)
      .eq('instructor_key', instructor.instructor_key);
    if (error) {
      setNote({ type: 'err', text: 'Could not remove — check your connection and try again.' });
    } else {
      setNote({ type: 'ok', text: `${lead.name ? lead.name.split(' ')[0] : 'Lead'} removed from the list.` });
    }
    setConfirming(null);
    setBusy(null);
    await load();
  }

  async function returnToList(lead) {
    setBusy(lead.id);
    setNote(null);
    // Mirror of the accept-promotion: a handoff that fell through means they
    // are NOT a current student — take them off the roster so they don't keep
    // trusted-tier instant claims. Best-effort (needs the roster DELETE
    // policy); the waitlist flip below is the operation that must succeed.
    try {
      await supabase.from('roster').delete()
        .eq('instructor_key', instructor.instructor_key)
        .eq('phone', lead.wa_id);
    } catch { /* policy missing → roster row lingers; harmless, flagged in notes */ }
    const { error } = await supabase
      .from('waitlist')
      .update({ status: 'waiting', updated_at: new Date().toISOString() })
      .eq('id', lead.id)
      .eq('instructor_key', instructor.instructor_key);
    setNote(error
      ? { type: 'err', text: 'Could not return them to the list — try again.' }
      : { type: 'ok',  text: `${lead.name ? lead.name.split(' ')[0] : 'Lead'} is back on the list with their original place.` });
    setBusy(null);
    await load();
  }

  if (!leads) return <div style={{ color: C.muted, padding: 20, fontFamily: 'Poppins, sans-serif' }}>Loading…</div>;

  const noteColors = {
    err:  { bg: C.redBg,   fg: C.red },
    warn: { bg: C.amberBg, fg: C.amber },
    ok:   { bg: C.greenBg, fg: C.green }
  };

  return (
    <div style={{ fontFamily: 'Poppins, sans-serif' }}>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>
        Waitlist
      </div>

      {/* Waiting | Accepted segment — accepted is the handoff HISTORY: who came
          through Visionry, with contact links that never disappear. */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {[['waiting', `Waiting (${leads.length})`], ['accepted', `Accepted (${accepted.length})`]].map(([id, label]) => (
          <button key={id} onClick={() => { setView(id); setConfirming(null); setNote(null); }}
            style={{
              background: view === id ? C.card2 : 'none',
              border: `1px solid ${view === id ? C.teal : C.border}`,
              borderRadius: 10, color: view === id ? C.tealSoft : C.muted,
              fontSize: 13.5, fontWeight: 600, padding: '9px 16px', minHeight: 40, cursor: 'pointer'
            }}>
            {label}
          </button>
        ))}
      </div>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>
        {isWaitlistMode
          ? 'Longest wait at the top. Accept someone when a space opens — they get a message, and you get their number to arrange the first lesson.'
          : "People who reached out while you were full. Accept one when a space opens — they'll be invited to set up and book, just like any new student."}
      </div>

      {note && (
        <div style={{
          marginBottom: 16, padding: '10px 12px', borderRadius: 8, fontSize: 13,
          background: (noteColors[note.type] || noteColors.ok).bg,
          color:      (noteColors[note.type] || noteColors.ok).fg,
          wordBreak: 'break-word'
        }}>
          {note.text}
        </div>
      )}

      {view === 'accepted' ? (
        accepted.length === 0 ? (
          <div style={{
            background: C.card, border: `1px solid ${C.border}`, borderRadius: 14,
            padding: 28, textAlign: 'center', color: C.muted, fontSize: 14
          }}>
            No accepted students yet.<br />
            <span style={{ fontSize: 12, color: C.faint }}>
              When you accept someone from the list, they'll stay here with their details and contact link.
            </span>
          </div>
        ) : (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: '4px 20px' }}>
            {accepted.map(lead => (
              <div key={lead.id} style={{
                display: 'flex', flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'stretch' : 'center', gap: 12,
                padding: '14px 0', borderBottom: `1px solid ${C.border}`
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: C.text }}>{lead.name || '—'}</div>
                  <div style={{ fontSize: 12, color: C.green, margin: '2px 0 6px' }}>
                    Accepted {waitedSince(lead.offered_at || lead.updated_at || lead.created_at)}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {lead.area         && <Chip tone="plain">📍 {lead.area}</Chip>}
                    {stageLabel(lead) && <Chip tone={stageTone(lead)}>{stageLabel(lead)}</Chip>}
                    {lead.availability && <Chip tone="plain">🗓 {lead.availability}{lead.avail_detail ? ` (${lead.avail_detail})` : ''}</Chip>}
                    <QualificationChips lead={lead} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                  <Btn as="a" href={`https://wa.me/${lead.wa_id}`} color={C.tealSoft} border={C.teal} fill={isMobile}>
                    💬 Message
                  </Btn>
                  <Btn onClick={() => returnToList(lead)} disabled={busy === lead.id} color={C.muted} border={C.border} fill={isMobile}>
                    {busy === lead.id ? 'Returning…' : 'Return to list'}
                  </Btn>
                </div>
              </div>
            ))}
          </div>
        )
      ) : leads.length === 0 ? (
        <div style={{
          background: C.card, border: `1px solid ${C.border}`, borderRadius: 14,
          padding: 28, textAlign: 'center', color: C.muted, fontSize: 14
        }}>
          No one waiting right now.<br />
          <span style={{ fontSize: 12, color: C.faint }}>
            Share your waitlist link (on the Home tab) — anyone who taps it goes straight onto this list.
          </span>
        </div>
      ) : (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: '4px 20px' }}>
          {leads.map(lead => {
            const incomplete = !lead.signup_completed_at;
            return (
              <div key={lead.id} style={{
                display: 'flex', flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'stretch' : 'center', gap: 12,
                padding: '14px 0', borderBottom: `1px solid ${C.border}`
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: lead.name ? C.text : C.muted }}>
                    {lead.name || 'Signup not finished'}
                  </div>
                  <div style={{ fontSize: 12, color: C.muted, margin: '2px 0 6px' }}>
                    Joined {waitedSince(lead.created_at)}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {lead.area         && <Chip tone="plain">📍 {lead.area}</Chip>}
                    {stageLabel(lead) && (
                      <Chip tone={stageTone(lead)}>{stageLabel(lead)}</Chip>
                    )}
                    {lead.availability && <Chip tone="plain">🗓 {lead.availability}{lead.avail_detail ? ` (${lead.avail_detail})` : ''}</Chip>}
                    <QualificationChips lead={lead} />
                    {incomplete        && <Chip tone="plain">…details pending</Chip>}
                  </div>
                </div>

                {confirming?.id === lead.id ? (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Btn
                      onClick={() => confirming.action === 'accept' ? accept(lead) : remove(lead)}
                      disabled={busy === lead.id}
                      filled
                      bg={confirming.action === 'accept' ? C.green : C.red}
                      color={confirming.action === 'accept' ? C.greenBg : C.redBg}
                      fill={isMobile}
                    >
                      {busy === lead.id
                        ? (confirming.action === 'accept' ? 'Accepting…' : 'Removing…')
                        : (confirming.action === 'accept' ? 'Confirm accept' : 'Confirm remove')}
                    </Btn>
                    <Btn onClick={() => setConfirming(null)} disabled={busy === lead.id}
                      filled bg={C.card2} color={C.text} fill={isMobile}>
                      Cancel
                    </Btn>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 8, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                    <Btn as="a" href={`https://wa.me/${lead.wa_id}`} color={C.muted} border={C.border} fill={isMobile}>
                      💬
                    </Btn>
                    <Btn onClick={() => { setConfirming({ id: lead.id, action: 'remove' }); setNote(null); }}
                      color={C.red} border={C.redBg} fill={isMobile}>
                      Remove
                    </Btn>
                    <Btn onClick={() => { setConfirming({ id: lead.id, action: 'accept' }); setNote(null); }}
                      color={C.tealSoft} border={C.teal} fill={isMobile} disabled={incomplete}>
                      Accept
                    </Btn>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
